/**
 * Liste de classe : import, détection de format, attributions des libellés.
 *
 * L'idéal visé par le commanditaire : un collage d'un élève par ligne, dont on
 * détecte seul le séparateur et l'ordre des colonnes. Détection **toujours**
 * confirmée par un aperçu avant validation (SPECIFICATION.md §7.3) : aucun
 * import silencieux.
 */

import { randomId } from './random.js';
import type { Person } from './types.js';

export type ColumnOrder = 'prenom_nom' | 'nom_prenom' | 'single';

export interface RosterOptions {
  /** Séparateur forcé, ou `auto` pour le déduire. */
  readonly separator?: string | 'auto';
  /** Présence d'une ligne d'en-tête, ou `auto`. */
  readonly hasHeader?: boolean | 'auto';
  /** Ordre des colonnes, ou `auto`. */
  readonly order?: ColumnOrder | 'auto';
}

export interface RosterDetection {
  readonly separator: string | null;
  readonly hasHeader: boolean;
  readonly order: ColumnOrder;
  readonly colonnes: number;
}

export interface RosterParseResult {
  readonly persons: Person[];
  readonly detection: RosterDetection;
  readonly warnings: readonly string[];
  readonly doublons: readonly string[];
}

/** Normalisation servant à comparer : accents, casse et espaces neutralisés. */
export function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Un jeton est « en capitales » s'il ressemble à un patronyme écrit en majuscules. */
function estEnCapitales(token: string): boolean {
  if (token.length < 2) return false;
  return /^[A-ZÀ-ÖØ-Þ][A-ZÀ-ÖØ-Þ'’-]*$/.test(token);
}

function separerNomPrenom(entree: string): { prenom: string; nom: string } {
  const tokens = entree.split(/\s+/).filter((token) => token.length > 0);
  const premier = tokens[0];
  if (premier === undefined) return { prenom: '', nom: '' };
  if (tokens.length === 1) return { prenom: premier, nom: '' };

  const debutCapitales = estEnCapitales(premier);
  const suiteCapitales = tokens.slice(1).some((token) => estEnCapitales(token));
  if (debutCapitales && !suiteCapitales) {
    return { prenom: tokens.slice(1).join(' '), nom: premier };
  }
  return { prenom: tokens.slice(0, -1).join(' '), nom: tokens[tokens.length - 1] as string };
}

const MOTS_ENTETE = /^(nom|noms|prenom|prénom|eleve|élève|nom de famille|nom d'usage|classe|sexe)$/i;

/**
 * Une cellule « ressemble à un patronyme » si son premier jeton est en
 * capitales. Couvre aussi bien `DUPONT` que `DUPONT Alice` dans une cellule.
 */
function ressemblePatronyme(cellule: string): boolean {
  const tokens = cellule.split(/\s+/).filter((token) => token.length > 0);
  const premier = tokens[0];
  if (premier === undefined) return false;
  if (tokens.length === 1) return estEnCapitales(premier);
  return estEnCapitales(premier) && !tokens.slice(1).some((token) => estEnCapitales(token));
}

/** Analyse un collage ou un fichier CSV. */
export function parseRoster(input: string, options: RosterOptions = {}): RosterParseResult {
  const warnings: string[] = [];
  const lignes = input
    .split(/\r?\n/)
    .map((ligne) => ligne.trim())
    .filter((ligne) => ligne.length > 0);

  const detectionVide: RosterDetection = { separator: null, hasHeader: false, order: 'single', colonnes: 1 };
  if (lignes.length === 0) {
    return { persons: [], detection: detectionVide, warnings: ['liste vide'], doublons: [] };
  }

  // 1. Séparateur : celui présent sur le plus de lignes, s'il est majoritaire.
  let separateur: string | null = null;
  if (options.separator && options.separator !== 'auto') {
    separateur = options.separator;
  } else {
    let meilleur = 0;
    for (const candidat of ['\t', ';', ',', '|']) {
      const presentes = lignes.filter((ligne) => ligne.includes(candidat)).length;
      if (presentes > meilleur) {
        meilleur = presentes;
        separateur = candidat;
      }
    }
    if (separateur !== null && meilleur < lignes.length * 0.6) {
      warnings.push(
        `Séparateur « ${separateur === '\t' ? 'tabulation' : separateur} » repéré sur seulement ${meilleur} ligne(s) sur ${lignes.length} : vérifiez l’aperçu.`,
      );
    }
  }

  const cellules = lignes.map((ligne) =>
    separateur ? ligne.split(separateur).map((champ) => champ.trim()) : [ligne],
  );

  // 2. En-tête : première ligne composée de mots-clés connus.
  let avecEntete: boolean;
  if (typeof options.hasHeader === 'boolean') {
    avecEntete = options.hasHeader;
  } else {
    const premiere = cellules[0] ?? [];
    avecEntete = premiere.length > 0 && premiere.every((champ) => MOTS_ENTETE.test(champ));
  }

  const corps = avecEntete ? cellules.slice(1) : cellules;
  if (corps.length === 0) {
    return { persons: [], detection: { ...detectionVide, separator: separateur, hasHeader: avecEntete }, warnings: [...warnings, 'aucune ligne de données'], doublons: [] };
  }

  const colonnes = Math.max(...corps.map((ligne) => ligne.length));

  // 3. Ordre des colonnes : une colonne en capitales trahit le patronyme.
  let ordre: ColumnOrder;
  if (options.order && options.order !== 'auto') {
    ordre = options.order;
  } else if (colonnes >= 2) {
    const ratioPatronymes = (index: number): number =>
      corps.filter((ligne) => ressemblePatronyme(ligne[index] ?? '')).length / corps.length;
    ordre = ratioPatronymes(0) > ratioPatronymes(1) + 0.3 ? 'nom_prenom' : 'prenom_nom';
  } else {
    ordre = 'single';
  }

  const doublons: string[] = [];
  const vus = new Set<string>();
  const bruts: { prenom: string; nom: string }[] = [];

  for (const ligne of corps) {
    let prenom = '';
    let nom = '';

    if (colonnes <= 1) {
      // Une seule colonne : « DUPONT Alice ». L'ordre forcé par l'enseignant
      // prime sur la déduction par les capitales, sinon un collage en
      // minuscules (« dupont alice ») resterait impossible à corriger.
      const complet = ligne[0] ?? '';
      const tokens = complet.split(/\s+/).filter((token) => token.length > 0);
      if (tokens.length >= 2 && ordre === 'nom_prenom') {
        nom = tokens[0] as string;
        prenom = tokens.slice(1).join(' ');
      } else if (tokens.length >= 2 && ordre === 'prenom_nom') {
        prenom = tokens.slice(0, -1).join(' ');
        nom = tokens[tokens.length - 1] as string;
      } else {
        const separes = separerNomPrenom(complet);
        prenom = separes.prenom;
        nom = separes.nom;
      }
    } else if (ordre === 'nom_prenom') {
      nom = ligne[0] ?? '';
      prenom = ligne.slice(1).join(' ').trim();
    } else {
      prenom = ligne[0] ?? '';
      nom = ligne.slice(1).join(' ').trim();
    }

    if (prenom.length === 0 && nom.length === 0) continue;

    const cle = normalizeText(`${prenom} ${nom}`);
    if (vus.has(cle)) {
      doublons.push(`${prenom} ${nom}`.trim());
      continue;
    }
    vus.add(cle);
    bruts.push({ prenom: prenom || nom, nom: prenom ? nom : '' });
  }

  if (doublons.length > 0) {
    warnings.push(
      `${doublons.length} ${doublons.length > 1 ? 'doublons exacts ignorés' : 'doublon exact ignoré'} : ${doublons.join(', ')}`,
    );
  }

  return {
    persons: assignLabels(bruts),
    detection: { separator: separateur, hasHeader: avecEntete, order: ordre, colonnes },
    warnings,
    doublons,
  };
}

/**
 * Attribue les libellés publics selon D5 :
 * `Prénom` si unique, sinon `Prénom N.`, puis `Prénom N. (k)` en cas de
 * collision d'initiale. Deux personnes ne portent jamais le même libellé.
 *
 * `ids` permet de **conserver les identifiants existants** lors d'une
 * correction de la liste importée : sans cela, corriger une faute de frappe
 * décalerait les `p_N` et les candidatures déjà déclarées pointeraient sur
 * d'autres personnes.
 */
export function assignLabels(
  entrees: readonly { prenom: string; nom: string }[],
  ids?: readonly string[],
): Person[] {
  const comptePrenom = new Map<string, number>();
  for (const entree of entrees) {
    const cle = normalizeText(entree.prenom);
    comptePrenom.set(cle, (comptePrenom.get(cle) ?? 0) + 1);
  }

  const bases = entrees.map((entree) => {
    const prenom = entree.prenom.trim() || entree.nom.trim();
    const unique = (comptePrenom.get(normalizeText(prenom)) ?? 0) <= 1;
    if (unique || entree.nom.trim().length === 0) return prenom;
    const initiale = normalizeText(entree.nom).charAt(0).toUpperCase();
    return `${prenom} ${initiale}.`;
  });

  const compteBase = new Map<string, number>();
  for (const base of bases) {
    const cle = normalizeText(base);
    compteBase.set(cle, (compteBase.get(cle) ?? 0) + 1);
  }

  const rang = new Map<string, number>();
  return bases.map((base, index) => {
    const cle = normalizeText(base);
    const enCollision = (compteBase.get(cle) ?? 0) > 1;
    let label = base;
    if (enCollision) {
      const suivant = (rang.get(cle) ?? 0) + 1;
      rang.set(cle, suivant);
      label = `${base} (${suivant})`;
    }
    const entree = entrees[index] as { prenom: string; nom: string };
    return {
      id: ids?.[index] ?? `p_${index + 1}`,
      prenom: entree.prenom.trim(),
      nom: entree.nom.trim(),
      label,
      ordre: index,
    };
  });
}

/** Identifiant de personne pour une saisie manuelle hors import. */
export function makePerson(prenom: string, nom: string, ordre: number): Person {
  return { id: randomId('p'), prenom, nom, label: prenom, ordre };
}
